/* eslint-disable unicorn/consistent-function-scoping */
import React, { FC, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from './styles';
import SimpleTableCustomCell from '../../simple-table-custom-cell';
import { headCells } from './head-cell';
import {
  getFaultCodesConfigurationStatuses,
  queueFaultCodeConfigurations,
  removeFaultCodeConfigurations,
} from './calls';
import { AppState } from '../../../store';
import { useMountEffect } from '../../../globals/hooks/use-mount-effect';
import DeleteIcon from '../../../assets/icons/delete-icon';
import theme from '../../../globals/theme';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';

const emptyFullFaultCode = {
  ecuId: '',
  faultCodeTypeIndicator: '',
  code: '',
  fmi: '',
  wci: '',
};

const testEcuid = (value: string) => value?.length !== 4;
const testFaultCode = (value: string) => value?.length !== 8;
const testFmi = (value: string) => value?.length !== 2;

interface InputsWithValidators {
  label: string;
  key: string;
  validator?: Function;
  helperText?: string;
  values?: any[];
}

const faultCodeInputData: InputsWithValidators[] = [
  { label: 'ECUID', key: 'ecuId', validator: testEcuid, helperText: 'must be 4 characters' },
  {
    label: 'Fault Code Type Indicator', key: 'faultCodeTypeIndicator', values: [
      {
        name: "00 (CAT Diagnostics)",
        value: "00"
      },
      {
        name: "01 (CAT Event)",
        value: "01"
      },
      {
        name: "02 (j1939 Fault)",
        value: "02"
      },
      {
        name: "04 (ABL Event)",
        value: "04"
      },
      {
        name: "05 (ABL Diagnostic)",
        value: "05"
      },
      {
        name: "123 (TPI Event)",
        value: "123"
      },
      {
        name: "124 (TPI Diagnostic)",
        value: "124"
      },
    ]
  },
  {
    label: 'Fault Code',
    key: 'code',
    validator: testFaultCode,
    helperText: 'must be 8 characters',
  },
  { label: 'FMI', key: 'fmi', validator: testFmi, helperText: 'must be 2 characters' },
  { label: 'WCI', key: 'wci', values: ['00', '01', '02', '03', '04', '05', 'FF'] },
];

const testInputs = (faultCodeInputs: { [key: string]: string }) =>
  faultCodeInputData.every((faultCodeInputDatum) => {
    if (faultCodeInputs[faultCodeInputDatum.key] && faultCodeInputDatum.validator)
      return !faultCodeInputDatum.validator(faultCodeInputs[faultCodeInputDatum.key]);
    return faultCodeInputs[faultCodeInputDatum.key];
  });

const reformatReturn = (configs: any) => {
  if (configs) {
    const { disableFaultCodes } = configs[0];
    let faultCodeList: any[] = [];
    disableFaultCodes.forEach((disableFaultCode: any) => {
      const { ecuId, disableFaultCodeConfigs } = disableFaultCode;
      disableFaultCodeConfigs.forEach((disableFaultCodeConfig: any) => {
        const { code, faultCodeTypeIndicator, fmi, wci, commands } = disableFaultCodeConfig;
        faultCodeList = [
          ...faultCodeList,
          {
            ecuId,
            faultCodeTypeIndicator,
            code,
            fmi,
            wci,
            status: commands[0].status,
          },
        ];
      });
    });
    return faultCodeList;
  }
  return null;
};

const FaultCodes: FC = () => {
  const classes = useStyles();
  const login = useSelector((state: AppState) => state.login);
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const [faultCodes, setFaultCodes] = useState<{ [key: string]: string }>(emptyFullFaultCode);
  const [tableData, setTableData] = useState<any[] | []>([]);
  const [disableQueButton, setDisableQueueButton] = useState(true);
  const dispatch = useDispatch();
  const snackbarCallback = ({ message, variant }: { message: string; variant: string }) => {
    dispatch(
      enqueueSnackbarAction({
        message,
        options: {
          variant,
        },
      }),
    );
  };
  const getFaultCodes = async () => {
    if (selectedEntity) {
      const configs = await getFaultCodesConfigurationStatuses({
        login,
        selectedEntity,
      });
      if (faultCodes) {
        const formatted = reformatReturn(configs);
        if (formatted) setTableData(formatted);
      }
    }
  };

  useMountEffect(() => {
    getFaultCodes();
  });

  const addToQueue = async () => {
    if (selectedEntity)
      await queueFaultCodeConfigurations({
        login,
        selectedEntity,
        faultCodeConfigs: faultCodes,
        snackbarCallback,
      });
    getFaultCodes();
  };

  const handleChange = (event: any, key: string) => {
    const newFaultCodes: { [key: string]: string } = {
      ...faultCodes,
      [`${key}`]: event?.target?.value,
    };
    setFaultCodes(newFaultCodes);
    testInputs(newFaultCodes);
    setDisableQueueButton(!testInputs(newFaultCodes));
  };

  const deleteAllFaultCodes = async () => {
    if (selectedEntity)
      await removeFaultCodeConfigurations({ login, selectedEntity, snackbarCallback });
    getFaultCodes();
  };

  return (
    <Box p={1} className={classes.noWrap} display="flex" flexDirection="column" width="100%">
      <Box display="flex" flexWrap="wrap">
        {faultCodeInputData.map((faultCodeInput: InputsWithValidators) => {
          const value = faultCodes[`${faultCodeInput.key}`];
          const valid =
            value.length !== 0 && faultCodeInput.validator && faultCodeInput.validator(value);
          return (
            <Box key={faultCodeInput.key} p={1} display="flex" alignItems="center">
              <Box px={1} pb={2}>
                <Typography variant="h6">{faultCodeInput.label}:</Typography>
              </Box>
              <Box p={1} height={67} display="flex" alignItems="flex-start">
                {faultCodeInput.validator && faultCodeInput.helperText && (
                  <Box mt={-2}>
                    <TextField
                      variant="standard"
                      error={valid}
                      helperText={valid && faultCodeInput.helperText}
                      className={classes.textLabel}
                      id={classes.textLabel}
                      label={faultCodeInput.label}
                      onChange={(event) => handleChange(event, faultCodeInput.key)}
                    />
                  </Box>
                )}
                {faultCodeInput.values && faultCodeInput.key === "faultCodeTypeIndicator" && (
                  <Select
                    variant="standard"
                    onChange={(event) => handleChange(event, faultCodeInput.key)}
                    labelId={faultCodeInput.label}
                    id="select"
                    value={value}
                  >
                    {faultCodeInput.values.map((item: any) => (
                      <MenuItem value={item.value}>{item.name}</MenuItem>
                    ))}
                  </Select>
                )}
                {faultCodeInput.values && faultCodeInput.key !== "faultCodeTypeIndicator" && (
                  <Select
                    variant="standard"
                    onChange={(event) => handleChange(event, faultCodeInput.key)}
                    labelId={faultCodeInput.label}
                    id="select"
                    value={value}
                  >
                    {faultCodeInput.values.map((value: string) => (
                      <MenuItem value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box display="flex" alignItems="center" flexGrow={1} justifyContent="flex-end" pr={2}>
        <Box>
          <Button
            disabled={disableQueButton}
            id="upload-add-to-queue'"
            onClick={addToQueue}
            variant="outlined"
            size="small"
            color="primary"
          >
            Add to Queue
          </Button>
        </Box>
        <Tooltip title="Delete all Fault Codes" enterDelay={700}>
          <Box pl={2}>
            <IconButton
              disabled={tableData.length === 0}
              onClick={deleteAllFaultCodes}
              size="large"
            >
              <DeleteIcon
                width="13px"
                height="13px"
                stroke={tableData.length > 0 ? theme.palette.grey[600] : theme.palette.grey[400]}
              />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
      {tableData.length > 0 && (
        <Grid item xs={12}>
          <Box mt={2} border={1} borderColor="grey.300">
            <SimpleTableCustomCell rows={tableData} headCells={headCells} maxHeight={300} />
          </Box>
        </Grid>
      )}
    </Box>
  );
};

export default FaultCodes;
